import * as React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button, InputText, InputTextArea, Panel, Select } from '../../../atoms'
import { Form } from '../../../molecules'
import SidebarLayout from '../../_layouts/sidebarLayout'
import SvgIcon from 'components/atoms/SvgIcon'
import { BODY_MAX_LENGTH } from './constants'

declare var window: interfaces.IWindow

// 施行日時は date + time を分離して持つので Form の管理対象外
const FIELDS = {
  name: 'name',
  message: 'message',
  body: 'body',
  status: 'status',
}

// effective_at (date + time) を「未来か過去か」判定するための utility
const isFutureDateTime = (date: string, time: string): boolean => {
  if (!date) {
    return false
  }
  const datetime = new Date(`${date}T${time || '00:00'}:00`)
  if (isNaN(datetime.getTime())) {
    return false
  }
  return datetime.getTime() > Date.now()
}

// 施行日時に応じて active 状態のラベルを「公開予定」「施行中」に切り替える
const getActiveLabel = (date: string, time: string): string => {
  if (!date) {
    return '公開予定 / 施行中'
  }
  return isFutureDateTime(date, time) ? '公開予定' : '施行中'
}

interface IProps {
  user: IJsonResponse
}

const AgentTermsVersionNew: React.FC<IProps> = props => {
  const { data: userData } = utilService.getDataFromJson(props.user)
  const [status, setStatus] = React.useState<string>('draft')
  const [effectiveDate, setEffectiveDate] = React.useState<string>('')
  const [effectiveTime, setEffectiveTime] = React.useState<string>('00:00')
  const [errors, setErrors] = React.useState<{ [key: string]: null | string }>({})

  const statusOptions = React.useMemo(
    () => [
      { value: 'draft', label: '下書き' },
      { value: 'active', label: getActiveLabel(effectiveDate, effectiveTime) },
    ],
    [effectiveDate, effectiveTime]
  )

  const handleSubmit = React.useCallback(
    async (_initialValues, values) => {
      const effectiveAt = effectiveDate ? `${effectiveDate} ${effectiveTime || '00:00'}` : ''
      const result = { ...values, status, effective_at: effectiveAt }
      const { flush } = await agentService.createTermsVersion(result)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      if (flush.type === 'success') {
        location.href = '/agent/terms_versions'
      }
    },
    [status, effectiveDate, effectiveTime]
  )

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel title="新規作成 - ガイド利用規約">
          <Form
            fields={FIELDS}
            handleSubmit={handleSubmit}
            handleUpdateForm={updatedErrors => setErrors(updatedErrors)}
          >
            <InputText
              required={true}
              name="name"
              defaultValue=""
              label="名前 (社内管理用)"
              placeholder="2026-05 個人情報改定"
              error={errors.name}
            />
            <S.FormItemWrapper>
              <InputTextArea
                required={false}
                name="message"
                defaultValue=""
                label="メッセージ (説明文)"
                placeholder="個人情報に関わる箇所について、データの取扱について定義を見直しました。具体的には..."
                error={errors.message}
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputTextArea
                required={true}
                name="body"
                defaultValue=""
                label="規約本文"
                placeholder={'第1条 (総則)\n本規約は、...\n\n第2条 (定義)\n本規約において...'}
                maxLength={BODY_MAX_LENGTH}
                error={errors.body}
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <S.DatetimeLabel>施行日時</S.DatetimeLabel>
              <S.DatetimeRow>
                <S.DateInput
                  type="date"
                  value={effectiveDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEffectiveDate(e.target.value)
                  }
                />
                <S.TimeInput
                  type="time"
                  value={effectiveTime}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEffectiveTime(e.target.value)
                  }
                />
              </S.DatetimeRow>
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Select
                name="status"
                label="状態"
                value={status}
                options={statusOptions}
                onChangeHandler={event => setStatus(event.target.value)}
              />
            </S.FormItemWrapper>
            <div className="Buttons">
              <Button>
                <a href="/agent/terms_versions">
                  <SvgIcon
                    name="keyboard_arrow_left"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  {I18n.t('generic.back_to_index')}
                </a>
              </Button>
              <Button primary={true}>{I18n.t('generic.create')}</Button>
            </div>
          </Form>
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.FormItemWrapper = styled.div`
  margin-top: 24px;
`

S.DatetimeLabel = styled.div`
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: bold;
  color: ${constants.COLORS.subText};
`

S.DatetimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

// ota_reservations の催行日時編集と同じ見た目に揃える
S.DateInput = styled.input`
  padding: 8px;
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 4px;
  font-size: 14px;
  &:disabled {
    background: ${constants.COLORS.gray};
    cursor: not-allowed;
  }
`

S.TimeInput = styled.input`
  padding: 8px;
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 4px;
  font-size: 14px;
  &:disabled {
    background: ${constants.COLORS.gray};
    cursor: not-allowed;
  }
`

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  .Buttons {
    position: relative;
    display: flex;
    justify-content: center;
    margin-top: 48px;

    .Button[type='submit'] {
      width: 160px;
    }

    .Button[type='button'] {
      position: absolute;
      left: 0;
      top: 0;
      display: flex;
      align-items: center;

      .icon {
        margin-left: -10px;
      }
    }
  }

  .InputText + .InputText {
    margin-top: 24px;
  }

  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    padding: 16px 12px;
  }
`

export default AgentTermsVersionNew
